"use client";

import * as React from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { Badge } from "../../components/ui/badge";
import controlsService, { ControlData } from "../../services/controlsService";
import { useTenancy } from "@/contexts/TenancyContext";

// Re-export the ControlData interface from controlsService
export type ControlOption = ControlData;

// Props for the ControlCombobox component
interface ControlComboboxProps {
  selected: (string | number)[];
  setSelected: (values: (string | number)[]) => void;
  placeholder?: string;
  className?: string;
  fullWidth?: boolean; // New prop to support fullWidth property from fieldsConfig
  onControlSelect?: (controlValue: string) => void;
  onControlDeSelect?: (controlValue: string) => void;
  onAllSelect?: () => void;
  onAllDeselect?: () => void;
  controls?: ControlOption[]; // Optionally pass controls directly
  fetchControls?: () => Promise<ControlOption[]>; // Or provide a function to fetch them
  dontShowSelectedControls?: boolean;
}

// Create a context to provide controls data
interface ControlsContextType {
  controls: ControlOption[];
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  standardFilters: string[];
  enabledStandards: string[];
  searchControls: (query: string, standardFilter?: string, page?: number, isModuleEnabled?) => Promise<void>;
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    pages: number;
  };
  setPage: (page: number) => void;
}

const ControlsContext = React.createContext<ControlsContextType>({
  controls: [],
  loading: false,
  error: null,
  refresh: async () => {},
  standardFilters: [],
  enabledStandards: [],
  searchControls: async () => {},
  pagination: {
    total: 0,
    page: 1,
    pageSize: 50,
    pages: 0
  },
  setPage: () => {}
});

// Provider component to fetch and manage controls data
export const ControlsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [controls, setControls] = React.useState<ControlOption[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);
  const [standardFilters, setStandardFilters] = React.useState<string[]>([]);
  const [enabledStandards, setEnabledStandards] = React.useState<string[]>([]);
  const [pagination, setPagination] = React.useState<{
    total: number;
    page: number;
    pageSize: number;
    pages: number;
  }>({ total: 0, page: 1, pageSize: 50, pages: 0 });
  const [currentQuery, setCurrentQuery] = React.useState<string>('');
  const [currentStandardFilter, setCurrentStandardFilter] = React.useState<string | undefined>();
  
  // Get tenancy context
  const { isModuleEnabled, enabledModules } = useTenancy();

  // Function to fetch all controls
  const fetchControls = React.useCallback(async () => {
    try {
      setLoading(true);
      
      if (!isModuleEnabled) {
        console.warn('isModuleEnabled function not available');
        return;
      }

      console.log('[ControlsService] isModuleEnabled:', isModuleEnabled);
      console.log('[ControlsService] enabledModules:', enabledModules);
      
      // Get all standards using the isModuleEnabled function
      const standards = await controlsService.getStandards(isModuleEnabled);
      setStandardFilters(standards);
      
      // Get enabled standards using the enabledModules array
      const enabled = await controlsService.getEnabledStandards(enabledModules);
      setEnabledStandards(enabled);
      
      // Get all controls for all enabled standards
      const allControls = await controlsService.getAllControls(isModuleEnabled);
      setControls(allControls);
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching controls:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      setLoading(false);
    }
  }, [isModuleEnabled, enabledModules]);

  // Function to search controls with pagination
  const searchControls = React.useCallback(async (query: string, standardFilter?: string, page: number = 1, isModuleEnabled?: IsModuleEnabled) => {
    try {
      setLoading(true);
      setCurrentQuery(query);
      setCurrentStandardFilter(standardFilter);
      
      const { results, total, page: currentPage, pageSize, pages } = 
        await controlsService.searchControls(query, standardFilter, page, 50, isModuleEnabled);
      
      // Only update controls if we have a search query or filter
      // This prevents overriding the initial controls with empty search results
      if (query || standardFilter) {
        setControls(results);
      } else if (page > 1) {
        // If it's a pagination request for the first page, update the controls
        setControls(results);
      }
      
      setPagination({ total, page: currentPage, pageSize, pages });
      setLoading(false);
    } catch (err) {
      console.error('Error searching controls:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      setLoading(false);
    }
  }, []);
  
  // Function to change page
  const setPage = React.useCallback((page: number) => {
    searchControls(currentQuery, currentStandardFilter, page, isModuleEnabled);
  }, [currentQuery, currentStandardFilter, searchControls, isModuleEnabled]);

  // Initial data loading
  React.useEffect(() => {
    fetchControls();
  }, [fetchControls]);

  return (
    <ControlsContext.Provider value={{ 
      controls, 
      loading, 
      error, 
      refresh: fetchControls,
      standardFilters,
      enabledStandards,
      searchControls,
      pagination,
      setPage
    }}>
      {children}
    </ControlsContext.Provider>
  );
};

// Hook to use the controls context
export const useControls = () => {
  const context = React.useContext(ControlsContext);
  if (context === undefined) {
    throw new Error('useControls must be used within a ControlsProvider');
  }
  return context;
};

// DED-1166: Component to display selected controls with "show more" functionality
const INITIAL_VISIBLE_COUNT = 8; // Show approximately 2 rows of controls initially

interface SelectedControlsDisplayProps {
  selectedControls: ControlOption[];
  onRemove: (controlValue: string) => void;
}

const SelectedControlsDisplay: React.FC<SelectedControlsDisplayProps> = ({
  selectedControls,
  onRemove
}) => {
  const [showAll, setShowAll] = React.useState(false);
  
  const visibleControls = showAll 
    ? selectedControls 
    : selectedControls.slice(0, INITIAL_VISIBLE_COUNT);
  
  const hiddenCount = selectedControls.length - INITIAL_VISIBLE_COUNT;
  const hasMore = hiddenCount > 0;
  
  return (
    <div className="mt-2">
      <div className="flex flex-wrap gap-1">
        {visibleControls.map(control => (
          <Badge 
            key={control.value} 
            variant="secondary"
            className="flex items-center gap-1 py-1"
          >
            <span className="text-xs font-medium">{control.value}</span>
            <span className="text-xs">-</span>
            <span className="text-xs truncate max-w-[150px]">
              {control.metadata?.controlName || control.label || 'Untitled Control'}
            </span>
            <button 
              className="ml-1 rounded-full hover:bg-primary/20 p-0.5"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(control.value);
              }}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        
        {/* Show more/less button */}
        {hasMore && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? 'Show less' : `+${hiddenCount} more`}
          </Button>
        )}
      </div>
    </div>
  );
};

// Main ControlCombobox component
export const ControlCombobox: React.FC<ControlComboboxProps> = ({
  selected,
  setSelected,
  dontShowSelectedControls = false,
  placeholder,
  className,
  fullWidth = false, // Default to false for backward compatibility
  onControlSelect,
  onControlDeSelect,
  onAllSelect,
  onAllDeselect,
  controls: propControls,
  fetchControls: propFetchControls
}) => {
  const [open, setOpen] = React.useState(false);
  const { isModuleEnabled } = useTenancy();
  // Reference to pagination section for scrolling
  const paginationRef = React.useRef<HTMLDivElement>(null);
  const { 
    controls: contextControls, 
    loading, 
    error, 
    refresh, 
    standardFilters, 
    searchControls, 
    pagination, 
    setPage 
  } = useControls();
  
  // Active standard filter state
  const [activeFilter, setActiveFilter] = React.useState<string | null>(null);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchDebounce, setSearchDebounce] = React.useState<NodeJS.Timeout | null>(null);
  
  // Ensure selected is always a valid array
  const safeSelected = Array.isArray(selected) ? selected : [];
  
  // Maintain a separate state for all selected controls to prevent them from disappearing when filtering
  const [selectedControls, setSelectedControls] = React.useState<ControlOption[]>([]);
  
  // Update selected controls when selected prop or controls change
  React.useEffect(() => {
    if (safeSelected.length === 0) {
      if (selectedControls.length > 0) {
        setSelectedControls([]);
      }
      return;
    }
    
    // Get all available controls (from props or context)
    const allAvailableControls = [...(propControls || []), ...(contextControls || [])];
    
    // Create a map of existing selected controls by value for quick lookup
    const existingSelectedMap = new Map(selectedControls.map(control => [String(control.value), control]));
    
    // Find all selected controls that exist in the available controls or are already in our selected state
    const updatedSelectedControls = safeSelected
      .map(value => {
        const valueStr = String(value);
        // First try to find in existing selected controls (to preserve full control data)
        if (existingSelectedMap.has(valueStr)) {
          return existingSelectedMap.get(valueStr);
        }
        // Otherwise try to find in available controls
        return allAvailableControls.find(c => String(c.value) === valueStr);
      })
      .filter((control): control is ControlOption => control !== undefined);
    
    // Only update if there are actual changes to prevent unnecessary re-renders
    if (updatedSelectedControls.length !== selectedControls.length ||
        !updatedSelectedControls.every((control, index) => 
          String(selectedControls[index]?.value) === String(control.value))) {
      setSelectedControls(updatedSelectedControls);
    }
  }, [safeSelected, propControls, contextControls, selectedControls]);
  
  // Get base controls from props or context
  const baseControls = [...(propControls || []), ...(contextControls || [])];
  
  // Get filtered controls based on search and filter
  let filteredControls = baseControls;
  
  if (searchQuery || activeFilter) {
    filteredControls = contextControls || [];
  }
  
  // Always include all selected controls in the list
  const allControls = [
    ...filteredControls,
    ...(selectedControls || [])
  ];
  
  // Remove duplicates (in case a selected control is already in the filtered list)
  const controlsMap = new Map();
  allControls.forEach(control => {
    if (control?.value !== undefined) {
      controlsMap.set(String(control.value), control);
    }
  });
  
  const controls = Array.from(controlsMap.values());
  
  // Debug logging for controls data
  React.useEffect(() => {
    // console.log('=== Controls Debug Info ===');
    // console.log('Controls in component:', {
    //   hasSearchOrFilter: !!(searchQuery || activeFilter),
    //   propControls: propControls?.length,
    //   contextControls: contextControls?.length,
    //   baseControls: baseControls?.length,
    //   filteredControls: filteredControls?.length,
    //   displayedControls: controls?.length,
    //   selectedControls: selectedControls?.length,
    //   selectedValues: safeSelected,
    //   searchQuery,
    //   activeFilter,
    //   loading,
    //   error: error?.message,
    //   standardFilters: standardFilters?.length
    // });
    
    if (controls && controls.length > 0) {
      // console.log('First control sample:', {
      //   value: controls[0].value,
      //   label: controls[0].label,
      //   standard: controls[0].standard,
      //   description: controls[0].description
      // });
    } else if (!loading) {
      // console.log('No controls available to display');
      // console.log('Has search or filter:', !!(searchQuery || activeFilter));
      // console.log('Context controls length:', contextControls?.length);
      // console.log('Prop controls length:', propControls?.length);
    }
  }, [propControls, contextControls, controls, safeSelected, searchQuery, activeFilter, loading, error, standardFilters]);
  
  // Handle search query and filter changes with debounce
  React.useEffect(() => {
    // Only search if we have a query or active filter
    if (searchQuery || activeFilter) {
      if (searchDebounce) {
        clearTimeout(searchDebounce);
      }
      
      const timeout = setTimeout(() => {
        searchControls(searchQuery, activeFilter || undefined, 1, isModuleEnabled);
      }, 300); // 300ms debounce
      
      setSearchDebounce(timeout);
      
      return () => {
        if (searchDebounce) {
          clearTimeout(searchDebounce);
        }
      };
    }
  }, [searchQuery, activeFilter, searchControls]);

  // Ensure selected is always a valid array
  // const safeSelected = Array.isArray(selected) ? selected : [];

  // Handle selection of a control
  const handleSelect = React.useCallback((controlValue: string) => {
    // Create a new array based on current selection
    if (safeSelected.includes(controlValue)) {
      // If already selected, remove it
      setSelected(safeSelected.filter(v => v !== controlValue));
      if (onControlDeSelect) onControlDeSelect(controlValue);
    } else {
      // Otherwise add it
      setSelected([...safeSelected, controlValue]);
      if (onControlSelect) onControlSelect(controlValue);
    }
  }, [safeSelected, setSelected, onControlSelect, onControlDeSelect]);

  // Handle select all functionality
  const handleSelectAll = React.useCallback(() => {
    // Check if all controls are currently selected
    const controlValues = controls.map(control => control.value);
    const allSelected = controlValues.length > 0 &&
      controlValues.every(value => safeSelected.includes(value));

    if (allSelected) {
      // If all are selected, deselect all controls
      const newSelected = safeSelected.filter(value => !controlValues.includes(value));
      setSelected(newSelected);
      if (onAllDeselect) onAllDeselect();
    } else {
      // Otherwise, select all controls (keeping existing selections)
      const newValues = controlValues.filter(value => !safeSelected.includes(value));
      setSelected([...safeSelected, ...newValues]);
      if (onAllSelect) onAllSelect();
    }
  }, [controls, safeSelected, setSelected, onAllSelect, onAllDeselect]);

  // Handle standard filter change
  const handleFilterChange = (standard: string) => {
    const newFilter = activeFilter === standard ? null : standard;
    setActiveFilter(newFilter);
    setPage(1);
    
    // When changing filters, ensure we don't lose selected controls
    if (newFilter) {
      // Get controls for the new standard
      searchControls(searchQuery, newFilter, 1, isModuleEnabled);
    } else {
      // If clearing filter, show all controls
      searchControls(searchQuery, undefined, 1, isModuleEnabled);
    }
  };

  // Effect to fetch any selected controls that aren't in the current list
  React.useEffect(() => {
    if (safeSelected.length > 0) {
      // Find which selected controls are missing from the current controls list
      const missingControls = safeSelected.filter(value => 
        !controls.some(control => control.value === value)
      );
      
      if (missingControls.length > 0) {
        // Fetch the missing controls
        controlsService.findControlsByIds(missingControls, isModuleEnabled)
          .then(results => {
            if (results.length > 0) {
              // We don't need to update selected as they're already selected,
              // just force a re-render if needed
              console.log(`Fetched ${results.length} missing controls`);
            }
          })
          .catch(err => {
            console.error(`Error fetching missing controls:`, err);
          });
      }
    }
  }, [safeSelected, controls]);

  // Scroll to pagination when page changes
  React.useEffect(() => {
    if (pagination.pages > 1 && paginationRef.current) {
      paginationRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [pagination.page]);

  return (
    <div className="space-y-4">
      {/* Control Combobox */}
      <Popover open={open} onOpenChange={setOpen} modal={true}>
        <PopoverTrigger asChild>
          <div className={cn("w-full", className)}>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-full min-w-0 justify-between text-sm md:text-base"
              type="button"
              disabled={loading}
            >
              {safeSelected.length > 0
                ? (
                  <div className="truncate text-left">
                    {`${safeSelected.length} control${safeSelected.length > 1 ? 's' : ''} selected`}
                  </div>
                )
                : (placeholder || "Select controls...")}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </div>
        </PopoverTrigger>
        <PopoverContent
          className={cn(
            "w-[--radix-popover-trigger-width] p-0 overflow-hidden",
            fullWidth ? "max-w-none" : "max-w-[500px]",
            "max-w-[calc(100vw-2rem)] md:max-w-[500px]"
          )}
          align="start"
          side="bottom"
          sideOffset={4}
          avoidCollisions={true}
          collisionPadding={8}
          style={{ 
            overscrollBehavior: 'contain'
          }}
        >
          <Command>
            <CommandInput 
              placeholder="Search controls..." 
              disabled={loading} 
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList 
              className="max-h-[300px] md:max-h-[400px] overflow-auto touch-pan-y" 
              onWheel={(e) => {
                // Prevent wheel events from propagating to parent elements
                e.stopPropagation();
                e.preventDefault();
                const target = e.currentTarget;
                target.scrollTop += e.deltaY;
              }}
              onTouchMove={(e) => {
                e.stopPropagation();
              }}
            >
              {/* Standard Filter Buttons */}
              <div className="flex flex-wrap gap-1 p-2 border-b overflow-auto max-h-[120px]">
                {standardFilters.map(standard => (
                  <Button
                    key={standard}
                    variant={activeFilter === standard ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleFilterChange(standard)}
                    className="text-xs mb-1 whitespace-nowrap"
                  >
                    {standard}
                  </Button>
                ))}
              </div>
              
              {loading ? (
                <CommandEmpty>Loading controls...</CommandEmpty>
              ) : controls.length === 0 ? (
                <CommandEmpty>
                  {searchQuery || activeFilter 
                    ? 'No controls match your search criteria.' 
                    : 'No controls available. Please check your configuration.'}
                </CommandEmpty>
              ) : null}
              <CommandGroup className="max-h-[200px] md:max-h-[300px] overflow-y-auto">
                {/* Select All option */}
                <CommandItem
                  value="select-all-option"
                  onSelect={handleSelectAll}
                  className="font-medium border-b"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      controls.length > 0 && 
                      controls.every(control => safeSelected.includes(control.value)) 
                        ? "opacity-100" 
                        : "opacity-0"
                    )}
                  />
                  Select All
                </CommandItem>

                {Array.isArray(controls) && controls.map((control: ControlOption) => {
                  // For clauses, use metadata.controlName if available, otherwise fall back to label
                  const displayLabel = control.metadata?.controlName || control.label;
                  // Use implementationGuidance from metadata if available, otherwise use description
                  const displayDescription = control.metadata?.implementationGuidance || control.description || control.metadata?.controlDesc || control.metadata?.controlPurpose || control.metadata?.controlJustification;
                  
                  return (
                    <CommandItem
                      key={control.value}
                      value={`${control.value} ${displayLabel || ''} ${displayDescription || ''}`}
                      onSelect={() => {
                        handleSelect(control.value);
                        setOpen(true); // Keep the popover open
                      }}
                      className="flex flex-col items-start py-2"
                    >
                      <div className="flex w-full items-center">
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4 flex-shrink-0",
                            safeSelected.includes(control.value) ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <div className="flex flex-col gap-1 overflow-hidden">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                              {control.standard}
                              {control.metadata?.isClause && ' Clause'}
                            </Badge>
                            <Badge variant="secondary" className="text-xs">
                              {control.value}
                            </Badge>
                          </div>
                          <div className="font-medium">{displayLabel}</div>
                          <div className="text-xs text-muted-foreground line-clamp-2">
                            {displayDescription || "No description available"}
                          </div>
                        </div>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              
              {/* Pagination controls */}
              {pagination.pages > 1 && (
                <div ref={paginationRef} className="flex items-center justify-between border-t px-4 py-2">
                  <div className="text-xs text-muted-foreground">
                    Showing {controls.length} of {pagination.total} results
                  </div>
                  <div className="flex items-center space-x-1">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => setPage(Math.max(1, pagination.page - 1))}
                      disabled={pagination.page === 1 || loading}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="m15 18-6-6 6-6" />
                      </svg>
                    </Button>
                    
                    <div className="text-xs">
                      {pagination.page} of {pagination.pages}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => setPage(Math.min(pagination.pages, pagination.page + 1))}
                      disabled={pagination.page === pagination.pages || loading}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="m9 18 6-6-6-6" />
                      </svg>
                    </Button>
                  </div>
                </div>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Selected Controls Display - DED-1166: Show limited controls with "show more" button */}
      {!dontShowSelectedControls && selectedControls.length > 0 && (
        <SelectedControlsDisplay 
          selectedControls={selectedControls}
          onRemove={handleSelect}
        />
      )}
    </div>
  );
};
